import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAxiosAuth0 } from '@shared/config/useAxiosAuth0';
import GenericButton from '@ui-components/Button/GenericButton';
import { GenericCard } from '@ui-components/Card/GenericCard';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericDropdown from '@ui-components/Dropdown/GenericDropdown';
import { fetchOdooOrder, listOdooOrders, fetchTenantServices } from './request';
import ServiceArchitectureGraph, { ServiceArchitectureGraphHandle } from './ServiceArchitectureGraph';
import {
  OdooOrder,
  OdooOrderLine,
  OdooOrderListItem,
  ProductCategory,
  ProductAssignment,
  PARENT_CATEGORIES,
  CHILD_CATEGORIES,
  FWAAS_CATEGORY,
  FWAAS_CHILD_CATEGORIES,
  SDWAN_HUB_CHILD_CATEGORIES,
  filterAssignableLines,
  DesignStatusResponse,
  SavedDesignPayload,
  DCIPortAssignment,
  TenantPortService,
  TenantInternetService,
  TenantCloudService
} from './types';
import { 
  getDataCentersIdsNames, 
  DataCenterItem 
} from '../ResourceManager/Hardware/request/request';

// Category color mapping - only use valid GenericTag variants
const categoryColors: Record<ProductCategory, 'success' | 'warning' | 'error' | 'default' | 'neutral'> = {
  cloud_connectivity: 'neutral',
  enterprise_internet: 'success',
  port: 'warning',
  last_mile: 'default',
  datacenter_interconnect: 'error',
  fwaas: 'warning',
  cpe: 'warning',
  ip_block: 'success',
  sdwan_hub: 'warning',
  sdwan_branch: 'neutral',
  other: 'default'
};

// Cloud connectivity options
const CLOUD_PROVIDERS = ['ConsoleConnect', 'Megaport', 'Equinix'] as const;
const CLOUD_PLATFORMS = ['AWS', 'GCP', 'Azure', 'OCI'] as const;

// Type for cloud connectivity configuration
interface CloudConnectivityConfig {
  datacenter: string;
  provider: string;
  cloud: string;
}

// State badge mapping
const stateVariants: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  sale: 'success',
  draft: 'warning',
  cancel: 'error',
  done: 'success'
};

// Base path for presales designer
const PRESALES_BASE_PATH = '/kontrol/admin-center/presales-designer';

export default function PresalesDesignerPage() {
  const api = useAxiosAuth0();
  const navigate = useNavigate();
  const { orderId: urlOrderId } = useParams<{ orderId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  
  // Determine if we're in detail view based on URL
  const isDetailView = Boolean(urlOrderId);
  
  // Order detail state
  const [order, setOrder] = useState<OdooOrder | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<ProductAssignment[]>([]);
  
  // Order list state
  const [orderList, setOrderList] = useState<OdooOrderListItem[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [stateFilter, setStateFilter] = useState<string>('sale');
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0, hasMore: false });
  
  // Datacenter state - for assigning infrastructure to our datacenters
  const [datacenters, setDatacenters] = useState<DataCenterItem[]>([]);
  const [parentDatacenterMap, setParentDatacenterMap] = useState<Record<number, string>>({}); // parentLineId -> datacenterId
  
  // Child service configuration state
  const [lastMileAddresses, setLastMileAddresses] = useState<Record<number, string>>({}); // childLineId -> address
  const [cloudConnectivityConfigs, setCloudConnectivityConfigs] = useState<Record<number, CloudConnectivityConfig>>({}); // childLineId -> config
  
  // DCI (Datacenter Interconnect) - connects two Ports (no datacenter, no child services)
  const [dciPortAssignments, setDciPortAssignments] = useState<Record<number, { portA: number | null; portB: number | null }>>({}); // dciLineId -> { portA, portB }

  // SD-WAN Hub roles (hubLineId -> 'primary' | 'secondary')
  const [sdwanHubRoles, setSdwanHubRoles] = useState<Record<number, 'primary' | 'secondary'>>({});

  // SD-WAN Branch primary hub assignments (branchLineId -> primaryHubLineId)
  const [sdwanBranchPrimaryHub, setSdwanBranchPrimaryHub] = useState<Record<number, number>>({});

  // Design status - tracks if design was already submitted
  const [designStatus, setDesignStatus] = useState<DesignStatusResponse | null>(null);

  // Presales comment - optional note added to all Jira tickets
  const [presalesComment, setPresalesComment] = useState<string>('');
  const [isDesignSubmitted, setIsDesignSubmitted] = useState(false);

  // Ref to the architecture graph for export
  const graphRef = useRef<ServiceArchitectureGraphHandle>(null);

  // Existing tenant services from previous orders
  const [existingServices, setExistingServices] = useState<{
    ports: TenantPortService[];
    internet: TenantInternetService[];
    cloud: TenantCloudService[];
  }>({ ports: [], internet: [], cloud: [] });
  const [isLoadingExistingServices, setIsLoadingExistingServices] = useState(false);
  const [existingServicesLoaded, setExistingServicesLoaded] = useState(false);
  const [existingSectionCollapsed, setExistingSectionCollapsed] = useState(true);

  // Assignments of current-order children to existing services from previous orders
  // Key: "port:{dbId}" or "internet:{dbId}", Value: array of child Odoo line IDs
  const [existingParentAssignments, setExistingParentAssignments] = useState<
    Record<string, number[]>
  >({});

  // Separate products by type for 4-column layout:
  // Column 1: Left services (Last Mile, CPE)
  // Column 2: Ports (infrastructure)
  // Column 3: FWaaS (intermediate)
  // Column 4: Right services (Cloud, Internet)
  const { parentLines, childLines, fwaasLines, dciLines, portLines, dciEndpointLines, sdwanHubLines, sdwanBranchLines } = useMemo(() => {
    if (!order) return {
      parentLines: [],
      childLines: [],
      fwaasLines: [],
      dciLines: [],
      portLines: [],
      dciEndpointLines: [],
      sdwanHubLines: [],
      sdwanBranchLines: []
    };

    // Filter out NRC products first (non-recurring charges, not actual services)
    const assignableLines = filterAssignableLines(order.orderLines);

    // Ports + SD-WAN Hubs (infrastructure - Column 2)
    const parents = assignableLines.filter(line =>
      PARENT_CATEGORIES.includes(line.product.category)
    );

    // FWaaS (intermediate - Column 3)
    const fwaas = assignableLines.filter(line =>
      line.product.category === FWAAS_CATEGORY
    );

    // All children for compatibility (excludes NRC products)
    const children = assignableLines.filter(line =>
      CHILD_CATEGORIES.includes(line.product.category)
    );

    // DCI lines (connects two ports)
    const dcis = assignableLines.filter(line =>
      line.product.category === 'datacenter_interconnect'
    );

    // Port lines only
    const ports = assignableLines.filter(line =>
      line.product.category === 'port'
    );

    // Port + SD-WAN Hub lines (for DCI endpoint selection)
    const dciEndpoints = assignableLines.filter(line =>
      line.product.category === 'port' || line.product.category === 'sdwan_hub'
    );

    // SD-WAN Hub lines (for branch primary hub selection)
    const sdwanHubs = assignableLines.filter(line =>
      line.product.category === 'sdwan_hub'
    );

    // SD-WAN Branch lines (assigned to hubs with primary/secondary)
    const sdwanBranches = assignableLines.filter(line =>
      line.product.category === 'sdwan_branch'
    );

    return {
      parentLines: parents,
      childLines: children,
      fwaasLines: fwaas,
      dciLines: dcis,
      portLines: ports,
      dciEndpointLines: dciEndpoints,
      sdwanHubLines: sdwanHubs,
      sdwanBranchLines: sdwanBranches
    };
  }, [order]);

  // IDs of children assigned to existing services from previous orders
  const existingAssignedIds = useMemo(() => {
    return new Set(Object.values(existingParentAssignments).flat());
  }, [existingParentAssignments]);

  // Get unassigned children (excluding those assigned to both current-order and existing parents)
  const unassignedChildren = useMemo(() => {
    const assignedIds = new Set(assignments.flatMap(a => a.childLineIds));
    return childLines.filter(line => !assignedIds.has(line.id) && !existingAssignedIds.has(line.id));
  }, [childLines, assignments, existingAssignedIds]);

  // Get children assigned to a specific parent
  const getAssignedChildren = useCallback((parentId: number) => {
    const assignment = assignments.find(a => a.parentLineId === parentId);
    if (!assignment) return [];
    return childLines.filter(line => assignment.childLineIds.includes(line.id));
  }, [assignments, childLines]);

  // Assign a child to a parent
  const assignChildToParent = useCallback((childId: number, parentId: number, parentType?: 'port' | 'sdwan_hub') => {
    setAssignments(prev => {
      // Remove child from any existing assignment
      const cleaned = prev.map(a => ({
        ...a,
        childLineIds: a.childLineIds.filter(id => id !== childId)
      })).filter(a => a.childLineIds.length > 0);

      // Find or create assignment for target parent
      const existingIndex = cleaned.findIndex(a => a.parentLineId === parentId);
      if (existingIndex >= 0) {
        cleaned[existingIndex] = {
          ...cleaned[existingIndex],
          childLineIds: [...cleaned[existingIndex].childLineIds, childId]
        };
      } else {
        cleaned.push({ parentLineId: parentId, childLineIds: [childId], parentType });
      }

      return cleaned;
    });
  }, []);

  // Remove child from parent
  const removeChildFromParent = useCallback((childId: number) => {
    setAssignments(prev => 
      prev.map(a => ({
        ...a,
        childLineIds: a.childLineIds.filter(id => id !== childId)
      })).filter(a => a.childLineIds.length > 0)
    );
    // Also remove from existing parent assignments
    setExistingParentAssignments(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        updated[key] = updated[key].filter(id => id !== childId);
        if (updated[key].length === 0) delete updated[key];
      }
      return updated;
    });
  }, []);

  // Get available children filtered by parent type
  const getAvailableChildrenForParent = useCallback((parentCategory: ProductCategory) => {
    if (parentCategory === 'sdwan_hub') {
      return unassignedChildren.filter(c => SDWAN_HUB_CHILD_CATEGORIES.includes(c.product.category));
    }
    // Port parent: exclude sdwan_branch from available children
    return unassignedChildren.filter(c =>
      c.product.category !== 'ip_block' && c.product.category !== 'sdwan_branch'
    );
  }, [unassignedChildren]);

  // Load existing services for the tenant (from previous orders)
  const loadExistingServices = useCallback(async (tenantId: number, excludeOrderId?: number) => {
    if (!api) return;
    setIsLoadingExistingServices(true);
    try {
      const response = await fetchTenantServices(api, tenantId, excludeOrderId);
      if (response.data.success) {
        setExistingServices(response.data.data.services);
        setExistingServicesLoaded(true);
      }
    } catch (err) {
      console.error('[PresalesDesigner] Failed to load existing services:', err);
    } finally {
      setIsLoadingExistingServices(false);
    }
  }, [api]);

  // Assign a child to an existing service from a previous order
  const assignChildToExistingParent = useCallback((childId: number, existingParentKey: string) => {
    // Remove from current-order assignments
    setAssignments(prev =>
      prev.map(a => ({
        ...a,
        childLineIds: a.childLineIds.filter(id => id !== childId)
      })).filter(a => a.childLineIds.length > 0)
    );
    // Remove from any other existing parent
    setExistingParentAssignments(prev => {
      const updated: Record<string, number[]> = {};
      for (const [key, ids] of Object.entries(prev)) {
        const filtered = ids.filter(id => id !== childId);
        if (filtered.length > 0) updated[key] = filtered;
      }
      // Add to target existing parent
      updated[existingParentKey] = [...(updated[existingParentKey] || []), childId];
      return updated;
    });
  }, []);

  // Get children assigned to an existing parent
  const getExistingParentChildren = useCallback((existingParentKey: string) => {
    const childIds = existingParentAssignments[existingParentKey] || [];
    if (!order) return [];
    const assignableLines = filterAssignableLines(order.orderLines);
    return assignableLines.filter(line => childIds.includes(line.id));
  }, [existingParentAssignments, order]);

  // Load order list
  const loadOrderList = useCallback(async (offset = 0) => {
    if (!api) return;
    
    setIsLoadingList(true);
    try {
      const response = await listOdooOrders(api, {
        limit: pagination.limit,
        offset,
        state: stateFilter as 'draft' | 'sent' | 'sale' | 'done' | 'cancel' | undefined || undefined,
        search: searchTerm || undefined
      });
      if (response.data.success) {
        setOrderList(response.data.data.orders);
        setPagination(response.data.data.pagination);
      }
    } catch {
      // Silently fail for list
    } finally {
      setIsLoadingList(false);
    }
  }, [api, stateFilter, searchTerm, pagination.limit]);

  // Load list on mount and when filters change
  useEffect(() => {
    loadOrderList(0);
  }, [stateFilter, searchTerm, api]);

  // Load datacenters for infrastructure assignment
  useEffect(() => {
    const fetchDatacenters = async () => {
      try {
        const token = await getAccessTokenSilently();
        console.log('[PresalesDesigner] Fetching datacenters with token:', token ? 'present' : 'missing');
        console.log('[PresalesDesigner] API_URL:', process.env.REACT_APP_API_URL);
        
        const response = await getDataCentersIdsNames(token);
        console.log('[PresalesDesigner] Datacenter response:', response);
        console.log('[PresalesDesigner] Datacenter response.data:', response.data);
        
        // Handle various response formats: 
        // - Direct array: DataCenterItem[]
        // - Wrapped: { data: DataCenterItem[] }
        // - API wrapper: { success: true, data: DataCenterItem[] }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const raw = response.data as any;
        let dcList: DataCenterItem[] = [];
        
        if (Array.isArray(raw)) {
          dcList = raw;
        } else if (raw?.data && Array.isArray(raw.data)) {
          dcList = raw.data;
        }
        
        console.log('[PresalesDesigner] Parsed datacenter list:', dcList);
        setDatacenters(dcList);
      } catch (err) {
        console.error('[PresalesDesigner] Error fetching datacenters:', err);
        setDatacenters([]);
      }
    };
    fetchDatacenters();
  }, [getAccessTokenSilently]);

  // Handler for assigning a datacenter to a parent product
  const handleAssignDatacenter = useCallback((parentLineId: number, datacenterId: string) => {
    setParentDatacenterMap(prev => ({
      ...prev,
      [parentLineId]: datacenterId
    }));
  }, []);

  // Handler for updating last mile address
  const handleLastMileAddressChange = useCallback((childLineId: number, address: string) => {
    setLastMileAddresses(prev => ({
      ...prev,
      [childLineId]: address
    }));
  }, []);

  // Handler for updating cloud connectivity config
  const handleCloudConfigChange = useCallback((
    childLineId: number, 
    field: keyof CloudConnectivityConfig, 
    value: string
  ) => {
    setCloudConnectivityConfigs(prev => ({
      ...prev,
      [childLineId]: {
        ...prev[childLineId],
        [field]: value
      }
    }));
  }, []);

  // Handler for DCI port assignments
  const handleDciPortAssignment = useCallback((
    dciLineId: number, 
    slot: 'portA' | 'portB', 
    portId: number | null
  ) => {
    setDciPortAssignments(prev => ({
      ...prev,
      [dciLineId]: {
        portA: prev[dciLineId]?.portA ?? null,
        portB: prev[dciLineId]?.portB ?? null,
        [slot]: portId
      }
    }));
  }, []);

  // Pagination handlers
  const handleNextPage = () => {
    if (pagination.hasMore) {
      loadOrderList(pagination.offset + pagination.limit);
    }
  };

  const handlePrevPage = () => {
    if (pagination.offset > 0) {
      loadOrderList(Math.max(0, pagination.offset - pagination.limit));
    }
  };

  // Navigate to order detail page
  const handleSelectOrder = useCallback((id: number) => {
    const targetPath = `${PRESALES_BASE_PATH}/${id}`;
    console.log('[PresalesDesigner] Navigating to:', targetPath);
    navigate(targetPath);
  }, [navigate]);

  // Go back to list view
  const handleBackToList = useCallback(() => {
    setOrder(null);
    setError(null);
    navigate(PRESALES_BASE_PATH);
  }, [navigate]);

  // State for submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<{
    ticketsCreated: number;
    tickets: Array<{ ticketKey: string; serviceType: string; action?: string }>;
  } | null>(null);
  
  // Dry run mode - preview tickets without creating them (for testing)
  const [isDryRunMode, setIsDryRunMode] = useState(false);
  const [dryRunPreview, setDryRunPreview] = useState<{
    servicesToTicket: number;
    ticketPreviews: Array<{ 
      serviceId: string; 
      serviceType: string;
      ticketKey?: string;
      action?: string;
      previewDescription?: string | null;
      jiraBody: {
        requestFieldValues: {
          summary: string;
          description: string;
        };
        serviceDeskId: string;
        requestTypeId: string;
      };
    }>;
    warnings?: Array<{ type: string; lineId: number; message: string }>;
  } | null>(null);

  // Validate that all services are properly assigned before submission
  const validateAssignments = useCallback(() => {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    if (!order) {
      return { isValid: false, errors: ['No order loaded'], warnings: [] };
    }

    // Get all service lines (excluding NRC products)
    const assignableLines = filterAssignableLines(order.orderLines);
    const portLines = assignableLines.filter(l => l.product.category === 'port');
    const fwaasLines = assignableLines.filter(l => l.product.category === 'fwaas');
    const dciLines = assignableLines.filter(l => l.product.category === 'datacenter_interconnect');
    const lastMileLines = assignableLines.filter(l => l.product.category === 'last_mile');
    const cpeLines = assignableLines.filter(l => l.product.category === 'cpe');
    const cloudInternetLines = assignableLines.filter(l => 
      ['cloud_connectivity', 'enterprise_internet'].includes(l.product.category)
    );

    // 1. Validate all Ports have datacenter assigned
    portLines.forEach(port => {
      if (!parentDatacenterMap[port.id]) {
        errors.push(`Port "${port.product.sku}" (${port.serviceId || port.id}) - Missing datacenter assignment`);
      }
    });

    // 1b. Validate all SD-WAN Branches have primary hub assigned
    const sdwanBranchLinesValidation = assignableLines.filter(l => l.product.category === 'sdwan_branch');
    const sdwanHubLinesValidation = assignableLines.filter(l => l.product.category === 'sdwan_hub');
    if (sdwanBranchLinesValidation.length > 0 && sdwanHubLinesValidation.length > 0) {
      sdwanBranchLinesValidation.forEach(branch => {
        if (!sdwanBranchPrimaryHub[branch.id]) {
          errors.push(`SD-WAN Branch "${branch.product.sku}" - Missing primary hub assignment`);
        }
      });
    }

    // 2. Validate all child services are assigned to a parent
    const allAssignedChildIds = assignments.flatMap(a => a.childLineIds);
    
    [...lastMileLines, ...cpeLines].forEach(lm => {
      if (!allAssignedChildIds.includes(lm.id)) {
        errors.push(`Last Mile "${lm.product.sku}" (${lm.serviceId || lm.id}) - Not assigned to any Port`);
      }
    });

    cloudInternetLines.forEach(ci => {
      if (!allAssignedChildIds.includes(ci.id)) {
        errors.push(`Cloud/Internet "${ci.product.sku}" (${ci.serviceId || ci.id}) - Not assigned to any Port/FWaaS`);
      }
    });

    // 3. Validate FWaaS are assigned to Ports (FWaaS is both child and parent)
    fwaasLines.forEach(fwaas => {
      if (!allAssignedChildIds.includes(fwaas.id)) {
        warnings.push(`FWaaS "${fwaas.product.sku}" (${fwaas.serviceId || fwaas.id}) - Not assigned to any Port`);
      }
    });

    // 4. Validate all DCIs have both Port A and Port B assigned
    dciLines.forEach(dci => {
      const dciAssignment = dciPortAssignments[dci.id];
      if (!dciAssignment?.portA || !dciAssignment?.portB) {
        errors.push(`DCI "${dci.product.sku}" (${dci.serviceId || dci.id}) - Missing Endpoint A and/or Endpoint B assignment`);
      }
    });

    // 5. Validate Last Mile addresses (REQUIRED)
    lastMileLines.forEach(lm => {
      if (!lastMileAddresses[lm.id]) {
        errors.push(`Last Mile "${lm.product.sku}" (${lm.serviceId || lm.id}) - Missing delivery address`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }, [order, assignments, parentDatacenterMap, dciPortAssignments, lastMileAddresses, sdwanBranchPrimaryHub]);

  // Submit assignments to backend
  const handleSubmitAssignments = useCallback(async () => {
    if (!order || !api) return;

    // Validate first
    const validation = validateAssignments();
    
    if (!validation.isValid) {
      setSubmitError(`Validation failed:\n${validation.errors.join('\n')}`);
      return;
    }

    // Show warnings but allow proceed
    if (validation.warnings.length > 0) {
      const proceed = window.confirm(
        `There are some warnings:\n\n${validation.warnings.join('\n')}\n\nDo you want to continue anyway?`
      );
      if (!proceed) return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      // Capture architecture diagram image for Jira attachment
      let architectureImageDataUrl: string | null = null;
      if (graphRef.current) {
        try {
          architectureImageDataUrl = await graphRef.current.getImageDataUrl('png');
          console.log('[PresalesDesigner] Architecture image captured:', architectureImageDataUrl ? 'success' : 'failed');
        } catch (error_) {
          console.warn('[PresalesDesigner] Could not capture architecture image:', error_);
        }
      }

      // Build FWaaS to Port assignments
      const fwaasToPortAssignments: Record<number, number> = {};
      order.orderLines
        .filter(l => l.product.category === 'fwaas')
        .forEach(fwaas => {
          // Find which parent this FWaaS is assigned to
          const parentAssignment = assignments.find(a => a.childLineIds.includes(fwaas.id));
          if (parentAssignment) {
            fwaasToPortAssignments[fwaas.id] = parentAssignment.parentLineId;
          }
        });

      // Build existing parent assignments for cross-order submission
      const existingParentAssignmentsList = Object.entries(existingParentAssignments)
        .filter(([, childIds]) => childIds.length > 0)
        .map(([key, childIds]) => {
          const [type, idStr] = key.split(':');
          return {
            parentServiceType: type,
            parentServiceDbId: parseInt(idStr, 10),
            childLineIds: childIds
          };
        });

      const payload = {
        odooOrderId: order.id,
        orderName: order.name,
        assignments,
        parentDatacenters: parentDatacenterMap,
        lastMileAddresses,
        cloudConnectivityConfigs,
        dciAssignments: dciPortAssignments,
        fwaasToPortAssignments,
        sdwanHubRoles,
        sdwanBranchPrimaryHub,
        existingParentAssignments: existingParentAssignmentsList,
        architectureImage: architectureImageDataUrl,
        presalesComment: presalesComment.trim() || undefined
      };

      console.log('[PresalesDesigner] Submitting assignments:', { 
        ...payload, 
        architectureImage: architectureImageDataUrl ? '[image data]' : null,
        isDryRun: isDryRunMode 
      });

      // Add dry run query parameter if enabled
      const endpoint = isDryRunMode ? '/presales/submit-design?dry=true' : '/presales/submit-design';
      const response = await api.post(endpoint, payload);

        if (response.data.success) {
        if (response.data.isDryRun) {
          // DRY RUN - Show preview without updating state
          setDryRunPreview({
            servicesToTicket: response.data.data.servicesToTicket || 0,
            ticketPreviews: response.data.data.ticketPreviews || [],
            warnings: response.data.data.warnings || []
          });
          console.log('[PresalesDesigner] DRY RUN preview:', response.data.data);
        } else {
          // REAL SUBMISSION
          const tickets = response.data.data.jiraTickets || [];
          setSubmitSuccess({
            ticketsCreated: tickets.length,
            tickets: tickets.map((t: { ticketKey: string; serviceType: string; action?: string }) => ({
              ticketKey: t.ticketKey,
              serviceType: t.serviceType,
              action: t.action || 'created'
            }))
          });
          
          // Update design status to show as submitted
          setIsDesignSubmitted(true);
          setDesignStatus((prev: DesignStatusResponse | null) => prev ? {
            ...prev,
            isDesignSubmitted: true,
            designSubmittedAt: new Date().toISOString(),
            savedDesign: {
              assignments,
              parentDatacenters: parentDatacenterMap,
              lastMileAddresses,
              cloudConnectivityConfigs,
              dciAssignments: dciPortAssignments as Record<number, DCIPortAssignment>,
              fwaasToPortAssignments: {},
              jiraTickets: tickets
            } as SavedDesignPayload
          } : null);
        }
      } else {
        setSubmitError(response.data.message || 'Failed to submit design');
      }
    } catch (err: unknown) {
      console.error('[PresalesDesigner] Submit error:', err);
      
      // Extract error details from Axios error response
      let errorMessage = 'Failed to submit design';
      
      // Check if it's an Axios error with response
      const axiosError = err as { response?: { status?: number; data?: { message?: string } } };
      
      if (axiosError.response?.status === 404) {
        errorMessage = `This order has not been processed yet.\n\nThe order must first go through the Odoo webhook process to create the necessary records before the design can be submitted.\n\nPlease ensure the order has been confirmed in Odoo and processed by the system.`;
      } else if (axiosError.response?.status === 409) {
        errorMessage = axiosError.response.data?.message || 'This design has already been submitted.';
      } else if (axiosError.response?.data?.message) {
        errorMessage = axiosError.response.data.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [order, api, validateAssignments, assignments, parentDatacenterMap, lastMileAddresses, cloudConnectivityConfigs, dciPortAssignments, isDryRunMode, presalesComment, existingParentAssignments]);

  // Load order when URL orderId changes
  useEffect(() => {
    const loadOrderFromUrl = async () => {
      console.log('[PresalesDesigner] URL orderId:', urlOrderId);
      if (!urlOrderId || !api) return;
      
      const id = Number.parseInt(urlOrderId, 10);
      if (Number.isNaN(id)) {
        setError('Invalid order ID');
        return;
      }
      console.log('[PresalesDesigner] Loading order from URL:', id);

      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch order details
        const response = await fetchOdooOrder(api, id);
        if (response.data.success) {
          setOrder(response.data.data);
          
          // Try to fetch saved design status
          try {
            const statusResponse = await api.get(`/presales/status/${id}`);
            if (statusResponse.data.success && statusResponse.data.data) {
              const status: DesignStatusResponse = statusResponse.data.data;
              setDesignStatus(status);
              setIsDesignSubmitted(status.isDesignSubmitted);
              
              // If design was saved, restore the configuration
              if (status.savedDesign) {
                const saved = status.savedDesign;
                console.log('[PresalesDesigner] Restoring saved design:', saved);
                
                // Restore assignments
                if (saved.assignments && saved.assignments.length > 0) {
                  setAssignments(saved.assignments);
                } else {
                  setAssignments([]);
                }
                
                // Restore datacenter map
                if (saved.parentDatacenters && Object.keys(saved.parentDatacenters).length > 0) {
                  // Convert string keys to numbers
                  const dcMap: Record<number, string> = {};
                  Object.entries(saved.parentDatacenters).forEach(([key, value]) => {
                    dcMap[Number(key)] = String(value);
                  });
                  setParentDatacenterMap(dcMap);
                } else {
                  setParentDatacenterMap({});
                }
                
                // Restore last mile addresses
                if (saved.lastMileAddresses && Object.keys(saved.lastMileAddresses).length > 0) {
                  const addrMap: Record<number, string> = {};
                  Object.entries(saved.lastMileAddresses).forEach(([key, value]) => {
                    addrMap[Number(key)] = String(value);
                  });
                  setLastMileAddresses(addrMap);
                } else {
                  setLastMileAddresses({});
                }
                
                // Restore cloud connectivity configs
                if (saved.cloudConnectivityConfigs && Object.keys(saved.cloudConnectivityConfigs).length > 0) {
                  const cloudMap: Record<number, CloudConnectivityConfig> = {};
                  Object.entries(saved.cloudConnectivityConfigs).forEach(([key, value]) => {
                    cloudMap[Number(key)] = value as CloudConnectivityConfig;
                  });
                  setCloudConnectivityConfigs(cloudMap);
                } else {
                  setCloudConnectivityConfigs({});
                }
                
                // Restore DCI port assignments
                if (saved.dciAssignments && Object.keys(saved.dciAssignments).length > 0) {
                  const dciMap: Record<number, { portA: number | null; portB: number | null }> = {};
                  Object.entries(saved.dciAssignments).forEach(([key, value]) => {
                    dciMap[Number(key)] = value as { portA: number | null; portB: number | null };
                  });
                  setDciPortAssignments(dciMap);
                } else {
                  setDciPortAssignments({});
                }

                // Restore SD-WAN Hub roles
                if (saved.sdwanHubRoles && Object.keys(saved.sdwanHubRoles).length > 0) {
                  const rolesMap: Record<number, 'primary' | 'secondary'> = {};
                  Object.entries(saved.sdwanHubRoles).forEach(([key, value]) => {
                    rolesMap[Number(key)] = value as 'primary' | 'secondary';
                  });
                  setSdwanHubRoles(rolesMap);
                } else {
                  setSdwanHubRoles({});
                }

                // Restore SD-WAN Branch primary hub assignments
                if (saved.sdwanBranchPrimaryHub && Object.keys(saved.sdwanBranchPrimaryHub).length > 0) {
                  const branchMap: Record<number, number> = {};
                  Object.entries(saved.sdwanBranchPrimaryHub).forEach(([key, value]) => {
                    branchMap[Number(key)] = Number(value);
                  });
                  setSdwanBranchPrimaryHub(branchMap);
                } else {
                  setSdwanBranchPrimaryHub({});
                }

              } else {
                // No saved design - reset all
                setAssignments([]);
                setParentDatacenterMap({});
                setLastMileAddresses({});
                setCloudConnectivityConfigs({});
                setDciPortAssignments({});
                setSdwanHubRoles({});
                setSdwanBranchPrimaryHub({});
              }
            } else {
              // No status found - reset
              setDesignStatus(null);
              setIsDesignSubmitted(false);
              setAssignments([]);
              setParentDatacenterMap({});
              setLastMileAddresses({});
              setCloudConnectivityConfigs({});
              setDciPortAssignments({});
              setSdwanHubRoles({});
              setSdwanBranchPrimaryHub({});
            }
          } catch {
            // Design status not found (order might not have been processed yet)
            console.log('[PresalesDesigner] No saved design status found');
            setDesignStatus(null);
            setIsDesignSubmitted(false);
            setAssignments([]);
            setParentDatacenterMap({});
            setLastMileAddresses({});
            setCloudConnectivityConfigs({});
            setDciPortAssignments({});
            setSdwanHubRoles({});
            setSdwanBranchPrimaryHub({});
          }

          // Auto-load existing tenant services if tenantId is available
          try {
            const statusResp = await api.get(`/presales/status/${id}`);
            const tId = statusResp?.data?.data?.tenantId;
            if (tId) {
              loadExistingServices(tId, id);
            }
          } catch {
            // tenantId not available, user can load manually later
          }
        } else {
          setError(response.data.message || 'Failed to fetch order');
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to fetch order';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadOrderFromUrl();
  }, [urlOrderId, api]);

  // ============================================
  // LIST VIEW (no orderId in URL)
  // ============================================
  if (!isDetailView) {
    return (
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-semantic-text-primary">
              Presales Designer
            </h1>
            <p className="text-sm text-semantic-text-secondary mt-1">
              Select an order to design service architecture
            </p>
          </div>
        </div>

        {/* Search & Order List Section */}
        <GenericCard isCollapsible={false} className="p-4">
          <div className="space-y-4">
            {/* Filters Row */}
            <div className="flex items-end gap-4">
              <div className="flex-1 max-w-xs">
                <label htmlFor="order-search" className="block text-sm font-medium text-semantic-text-secondary mb-1">
                  Search Orders
                </label>
                <input
                  id="order-search"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by order name (S00xxx)..."
                  className="
                    w-full px-3 py-2
                    border border-semantic-border-secondary
                    rounded-md
                    bg-semantic-bg-secondary
                    text-semantic-text-primary
                    placeholder:text-semantic-text-disabled
                    focus:outline-none focus:ring-2 focus:ring-brand-primary
                  "
                />
              </div>
              <div className="w-40">
                <label htmlFor="state-filter" className="block text-sm font-medium text-semantic-text-secondary mb-1">
                  State
                </label>
                <select
                  id="state-filter"
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="
                    w-full px-3 py-2
                    border border-semantic-border-secondary
                    rounded-md
                    bg-semantic-bg-secondary
                    text-semantic-text-primary
                    focus:outline-none focus:ring-2 focus:ring-brand-primary
                  "
                >
                  <option value="">All States</option>
                  <option value="draft">Quotation</option>
                  <option value="sale">Sales Order</option>
                  <option value="done">Locked</option>
                  <option value="cancel">Cancelled</option>
                </select>
              </div>
              <div className="flex flex-col justify-end">
                <label className="block text-sm font-medium text-semantic-text-secondary mb-1 invisible">
                  Action
                </label>
                <button
                  onClick={() => loadOrderList(0)}
                  disabled={isLoadingList}
                  className="
                    px-4 py-2
                    border border-semantic-border-secondary
                    rounded-md
                    bg-semantic-bg-secondary
                    text-semantic-text-primary
                    hover:bg-semantic-bg-hover
                    focus:outline-none focus:ring-2 focus:ring-brand-primary
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors
                  "
                >
                  {isLoadingList ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-semantic-error">{error}</p>
            )}

            {/* Orders Table */}
            <div className="border border-semantic-border-primary rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-semantic-bg-secondary">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-semantic-text-secondary">Order</th>
                    <th className="px-4 py-3 text-left font-medium text-semantic-text-secondary">Partner</th>
                    <th className="px-4 py-3 text-left font-medium text-semantic-text-secondary">Partner ID</th>
                    <th className="px-4 py-3 text-left font-medium text-semantic-text-secondary">Customer</th>
                    <th className="px-4 py-3 text-left font-medium text-semantic-text-secondary">Customer ID</th>
                    <th className="px-4 py-3 text-left font-medium text-semantic-text-secondary">Status</th>
                    <th className="px-4 py-3 text-right font-medium text-semantic-text-secondary">Total</th>
                    <th className="px-4 py-3 text-left font-medium text-semantic-text-secondary">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-semantic-border-primary">
                  {isLoadingList && (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-semantic-text-secondary">
                        Loading orders...
                      </td>
                    </tr>
                  )}
                  {!isLoadingList && orderList.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-semantic-text-secondary">
                        No orders found
                      </td>
                    </tr>
                  )}
                  {!isLoadingList && orderList.length > 0 && orderList.map((o) => (
                    <tr 
                      key={o.id}
                      className="hover:bg-semantic-bg-hover transition-colors"
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleSelectOrder(o.id)}
                          className="font-medium text-brand-primary hover:underline cursor-pointer"
                        >
                          {o.name}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-semantic-text-primary truncate max-w-[200px]">
                        {o.partner?.name || '-'}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-semantic-text-secondary">
                        {o.pid || '-'}
                      </td>
                      <td className="px-4 py-3 text-semantic-text-primary truncate max-w-[200px]">
                        {o.shippingPartner?.name || o.partner?.name || '-'}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-semantic-text-secondary">
                        {o.cid || '-'}
                      </td>
                      <td className="px-4 py-3">
                        {o.processingStatus ? (
                          <GenericTag
                            label={o.processingStatus === 'pending_presales' ? 'Pending Design' : 
                                   o.processingStatus === 'completed' ? 'Completed' :
                                   o.processingStatus === 'failed' ? 'Failed' :
                                   o.processingStatus === 'processing' ? 'Processing' : o.processingStatus}
                            variant={o.processingStatus === 'pending_presales' ? 'warning' :
                                    o.processingStatus === 'completed' ? 'success' :
                                    o.processingStatus === 'failed' ? 'error' :
                                    o.processingStatus === 'processing' ? 'neutral' : 'default'}
                          />
                        ) : (
                          <span className="text-xs text-semantic-text-tertiary">Not processed</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        ${o.amountTotal.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-semantic-text-secondary text-xs">
                        {o.dateOrder ? new Date(o.dateOrder).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between pt-4 border-t border-semantic-border-primary">
              <div className="text-sm text-semantic-text-secondary">
                Showing {pagination.offset + 1} - {Math.min(pagination.offset + orderList.length, pagination.total)} of {pagination.total} orders
              </div>
              <div className="flex items-center gap-2">
                <GenericButton
                  label="Previous"
                  onClick={handlePrevPage}
                  disabled={pagination.offset === 0 || isLoadingList}
                  variant="outlined"
                  size="sm"
                />
                <GenericButton
                  label="Next"
                  onClick={handleNextPage}
                  disabled={!pagination.hasMore || isLoadingList}
                  variant="outlined"
                  size="sm"
                />
              </div>
            </div>
          </div>
        </GenericCard>

        {/* Loading indicator */}
        {isLoading && (
          <div className="p-8 text-center text-semantic-text-secondary">
            <p>Loading order...</p>
          </div>
        )}
      </div>
    );
  }

  // ============================================
  // DETAIL VIEW
  // ============================================
  return (
    <div className="p-6 space-y-6">
      {/* Back Button + Header */}
      <div className="flex items-center gap-4">
        <GenericButton
          label="Back to Orders"
          onClick={handleBackToList}
          variant="outlined"
          size="sm"
        />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-semantic-text-primary">
            Service Designer
          </h1>
          <p className="text-sm text-semantic-text-secondary mt-1">
            Design and assign services for order {order?.name}
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {isLoading && (
        <div className="p-8 text-center text-semantic-text-secondary">
          <p>Loading order details...</p>
        </div>
      )}

      {/* Order Details */}
      {order && (
        <div className="space-y-6">
          {/* Order Header */}
          <GenericCard isCollapsible={false} className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold text-semantic-text-primary">
                    {order.name}
                  </h2>
                  <GenericTag
                    label={order.stateLabel}
                    variant={stateVariants[order.state] || 'default'}
                    size="small"
                  />
                </div>
                <p className="text-sm text-semantic-text-secondary mt-1">
                  <span className="text-semantic-text-tertiary">Partner:</span> {order.partner?.name || 'No partner'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-semantic-text-primary">
                  {order.currency?.name || 'USD'} {order.amountTotal.toLocaleString()}
                </p>
                <p className="text-sm text-semantic-text-secondary">
                  {order.dateOrder}
                </p>
              </div>
            </div>

            {/* Order Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6 pt-6 border-t border-semantic-border-secondary">
              <div>
                <p className="text-xs text-semantic-text-secondary uppercase tracking-wide">Final Customer</p>
                <p className="text-sm font-medium text-semantic-text-primary mt-1">{order.shippingPartner?.name || order.partner?.name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-semantic-text-secondary uppercase tracking-wide">Partner ID</p>
                <p className="text-sm font-medium text-semantic-text-primary mt-1">{order.pid || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-semantic-text-secondary uppercase tracking-wide">Customer ID</p>
                <p className="text-sm font-medium text-semantic-text-primary mt-1">{order.cid || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-semantic-text-secondary uppercase tracking-wide">Sales Rep</p>
                <p className="text-sm font-medium text-semantic-text-primary mt-1">
                  {order.salesUser?.name || '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-semantic-text-secondary uppercase tracking-wide">Presales</p>
                <p className="text-sm font-medium text-semantic-text-primary mt-1">
                  {order.presales?.name || '-'}
                </p>
                {order.presalesEmail && (
                  <p className="text-xs text-semantic-text-secondary mt-0.5">
                    {order.presalesEmail}
                  </p>
                )}
              </div>
            </div>
          </GenericCard>

          {/* Stats Summary */}
          <GenericCard isCollapsible={false} className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-semantic-text-secondary uppercase tracking-wide">Total Services</p>
                <p className="text-2xl font-bold text-semantic-text-primary mt-1">
                  {order.stats.totalLines}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {Object.entries(order.linesByCategory).map(([category, data]) => (
                  <div key={category} className="flex items-center gap-2 bg-semantic-bg-secondary px-3 py-1.5 rounded-sm">
                    <span className="text-xs font-medium text-semantic-text-secondary uppercase">
                      {data.label}
                    </span>
                    <span className="text-sm font-bold text-semantic-text-primary">
                      {data.lines.length}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </GenericCard>

          {/* Product Hierarchy Assignment */}
          {(parentLines.length > 0 || childLines.length > 0 || dciLines.length > 0) && (
            <div className="bg-semantic-bg-primary border border-semantic-border-primary rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-semantic-text-primary">
                    Service Assignment
                  </h3>
                  <p className="text-sm text-semantic-text-secondary mt-1">
                    Assign child services (Cloud, Internet, Last Mile, IP Block) to parent infrastructure (Port, FWaaS, Backbone, Internet)
                  </p>
                </div>
                {assignments.length > 0 && (
                  <GenericButton
                    label="Clear All"
                    onClick={() => setAssignments([])}
                    variant="secondary"
                    size="sm"
                  />
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Parent Products Column */}
                <div>
                  <h4 className="text-sm font-medium text-semantic-text-secondary mb-3 uppercase tracking-wide">
                    Infrastructure ({parentLines.length})
                  </h4>
                  <div className="space-y-3">
                    {parentLines.length === 0 ? (
                      <p className="text-sm text-semantic-text-disabled italic">No parent products in this order</p>
                    ) : (
                      parentLines.map(parent => {
                        const assignedChildren = getAssignedChildren(parent.id);
                        const selectedDC = parentDatacenterMap[parent.id];
                        const selectedDCName = selectedDC 
                          ? datacenters.find(dc => dc.datacenterId === selectedDC)?.name || selectedDC
                          : null;
                        return (
                          <div 
                            key={parent.id} 
                            className="border border-semantic-border-secondary rounded-lg p-4 bg-semantic-bg-secondary"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <GenericTag
                                    label={parent.product.categoryLabel}
                                    variant={categoryColors[parent.product.category] || 'default'}
                                    size="small"
                                  />
                                  <span className="text-xs text-semantic-text-secondary">
                                    Odoo: {parent.origin?.name || 'No location'}
                                  </span>
                                </div>
                                <p className="text-sm font-medium text-semantic-text-primary mt-1">
                                  {parent.product.sku || parent.product.name}
                                </p>
                                <p className="text-xs text-semantic-text-secondary">
                                  {parent.capacity ? `${parent.capacity} Mbps` : ''} 
                                  {parent.serviceId ? ` | ${parent.serviceId}` : ''}
                                </p>
                              </div>
                              <span className="text-sm font-medium text-semantic-text-primary">
                                ${parent.subtotal.toLocaleString()}
                              </span>
                            </div>

                            {/* Datacenter Selector (for Port, FWaaS - not SD-WAN Hub) */}
                            {parent.product.category !== 'sdwan_hub' && (
                            <div className="mb-3">
                              <p className="text-xs font-medium text-semantic-text-secondary mb-1">
                                Assign to Datacenter
                              </p>
                              <GenericDropdown
                                items={datacenters.map(dc => dc.name)}
                                defaultLabel={selectedDCName || "Select datacenter..."}
                                maxWidthClass="w-full"
                                dropdownMaxHeightClass="max-h-48"
                                fixedPosition={true}
                                onSelect={(selected) => {
                                  const dc = datacenters.find(d => d.name === selected);
                                  if (dc) {
                                    handleAssignDatacenter(parent.id, dc.datacenterId);
                                  }
                                }}
                              />
                              {selectedDCName && (
                                <p className="text-xs text-green-600 mt-1">
                                  ✓ {selectedDCName}
                                </p>
                              )}
                            </div>
                            )}

                            {/* Assigned Children (only for Port, FWaaS - not DCI) */}
                            {assignedChildren.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-semantic-border-secondary">
                                <p className="text-xs text-semantic-text-secondary mb-2">
                                  Assigned Services ({assignedChildren.length})
                                </p>
                                <div className="space-y-3">
                                  {assignedChildren.map(child => {
                                    const isLastMile = child.product.category === 'last_mile';
                                    const isCloudConnectivity = child.product.category === 'cloud_connectivity';
                                    const cloudConfig = cloudConnectivityConfigs[child.id] || {};
                                    
                                    return (
                                      <div 
                                        key={child.id}
                                        className="bg-semantic-bg-primary rounded-lg border border-semantic-border-secondary overflow-hidden"
                                      >
                                        {/* Header row */}
                                        <div className="flex items-center justify-between px-3 py-2">
                                          <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <GenericTag
                                              label={child.product.categoryLabel}
                                              variant={categoryColors[child.product.category] || 'default'}
                                              size="small"
                                            />
                                            <span className="text-xs text-semantic-text-primary truncate">
                                              {child.product.sku || child.product.name}
                                            </span>
                                            {child.destination && (
                                              <span className="text-xs text-semantic-text-secondary">
                                                → {child.destination.name}
                                              </span>
                                            )}
                                          </div>
                                          <button
                                            onClick={() => removeChildFromParent(child.id)}
                                            className="ml-2 text-semantic-error hover:text-red-700 text-xs"
                                            title="Remove assignment"
                                          >
                                            ✕
                                          </button>
                                        </div>

                                        {/* Last Mile - Address Input */}
                                        {isLastMile && (
                                          <div className="px-3 pb-3 pt-1 border-t border-semantic-border-secondary bg-semantic-bg-secondary">
                                            <label 
                                              htmlFor={`assigned-address-${child.id}`}
                                              className="block text-xs font-medium text-semantic-text-secondary mb-1"
                                            >
                                              Final Address
                                            </label>
                                            <input
                                              id={`assigned-address-${child.id}`}
                                              type="text"
                                              value={lastMileAddresses[child.id] || ''}
                                              onChange={(e) => handleLastMileAddressChange(child.id, e.target.value)}
                                              placeholder="Enter delivery address..."
                                              className="
                                                w-full px-2 py-1.5 text-xs
                                                border border-semantic-border-secondary rounded
                                                bg-semantic-bg-primary text-semantic-text-primary
                                                placeholder:text-semantic-text-disabled
                                                focus:outline-none focus:ring-2 focus:ring-brand-primary
                                              "
                                            />
                                          </div>
                                        )}

                                        {/* Cloud Connectivity - Datacenter, Provider, Cloud */}
                                        {isCloudConnectivity && (
                                          <div className="px-3 pb-3 pt-1 border-t border-semantic-border-secondary bg-semantic-bg-secondary">
                                            <div className="space-y-2">
                                              {/* Datacenter */}
                                              <div>
                                                <p className="text-xs font-medium text-semantic-text-secondary mb-1">Datacenter</p>
                                                <GenericDropdown
                                                  items={datacenters.map(dc => dc.name)}
                                                  defaultLabel={
                                                    cloudConfig.datacenter 
                                                      ? datacenters.find(dc => dc.datacenterId === cloudConfig.datacenter)?.name || 'Select...'
                                                      : 'Select...'
                                                  }
                                                  maxWidthClass="w-full"
                                                  dropdownMaxHeightClass="max-h-36"
                                                  fixedPosition={true}
                                                  onSelect={(selected) => {
                                                    const dc = datacenters.find(d => d.name === selected);
                                                    if (dc) {
                                                      handleCloudConfigChange(child.id, 'datacenter', dc.datacenterId);
                                                    }
                                                  }}
                                                />
                                              </div>
                                              {/* Provider */}
                                              <div>
                                                <p className="text-xs font-medium text-semantic-text-secondary mb-1">Provider</p>
                                                <GenericDropdown
                                                  items={[...CLOUD_PROVIDERS]}
                                                  defaultLabel={cloudConfig.provider || 'Select...'}
                                                  maxWidthClass="w-full"
                                                  dropdownMaxHeightClass="max-h-36"
                                                  fixedPosition={true}
                                                  onSelect={(selected) => {
                                                    handleCloudConfigChange(child.id, 'provider', selected);
                                                  }}
                                                />
                                              </div>
                                              {/* Cloud Platform */}
                                              <div>
                                                <p className="text-xs font-medium text-semantic-text-secondary mb-1">Cloud</p>
                                                <GenericDropdown
                                                  items={[...CLOUD_PLATFORMS]}
                                                  defaultLabel={cloudConfig.cloud || 'Select...'}
                                                  maxWidthClass="w-full"
                                                  dropdownMaxHeightClass="max-h-36"
                                                  fixedPosition={true}
                                                  onSelect={(selected) => {
                                                    handleCloudConfigChange(child.id, 'cloud', selected);
                                                  }}
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Drop zone hint - filtered by parent type */}
                            {getAvailableChildrenForParent(parent.product.category).length > 0 && (
                              <div className="mt-3 pt-3 border-t border-dashed border-semantic-border-secondary">
                                <GenericDropdown
                                  items={getAvailableChildrenForParent(parent.product.category).map(child =>
                                    `${child.product.categoryLabel}: ${child.product.sku || child.product.name}${child.destination ? ` → ${child.destination.name}` : ''}${child.quantity > 1 ? ` (x${child.quantity})` : ''}`
                                  )}
                                  defaultLabel="+ Assign a service..."
                                  maxWidthClass="w-full"
                                  dropdownMaxHeightClass="max-h-48"
                                  fixedPosition={true}
                                  onSelect={(selected) => {
                                    const child = getAvailableChildrenForParent(parent.product.category).find(c =>
                                      `${c.product.categoryLabel}: ${c.product.sku || c.product.name}${c.destination ? ` → ${c.destination.name}` : ''}${c.quantity > 1 ? ` (x${c.quantity})` : ''}` === selected
                                    );
                                    if (child) {
                                      const pType = parent.product.category === 'sdwan_hub' ? 'sdwan_hub' as const : 'port' as const;
                                      assignChildToParent(child.id, parent.id, pType);
                                    }
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Existing Services from Previous Orders (collapsible) */}
                  <div className="mt-6 pt-4 border-t border-semantic-border-secondary">
                    <button
                      type="button"
                      className="flex items-center justify-between w-full text-left mb-3"
                      onClick={() => setExistingSectionCollapsed(prev => !prev)}
                    >
                      <h4 className="text-sm font-medium text-semantic-text-secondary uppercase tracking-wide flex items-center gap-2">
                        <span className={`inline-block transition-transform duration-200 ${existingSectionCollapsed ? '' : 'rotate-90'}`}>
                          &#9654;
                        </span>
                        Existing Services (Previous Orders)
                      </h4>
                      {designStatus?.tenantId && !existingServicesLoaded && (
                        <GenericButton
                          label={isLoadingExistingServices ? 'Loading...' : 'Load Services'}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setExistingSectionCollapsed(false);
                            if (designStatus?.tenantId) loadExistingServices(designStatus.tenantId, order?.id);
                          }}
                          variant="secondary"
                          size="sm"
                          disabled={isLoadingExistingServices}
                        />
                      )}
                    </button>

                    {!existingSectionCollapsed && !existingServicesLoaded && !designStatus?.tenantId && (
                      <p className="text-xs text-semantic-text-disabled italic">
                        Tenant not resolved yet. The order must be processed first.
                      </p>
                    )}

                    {!existingSectionCollapsed && existingServicesLoaded && existingServices.ports.length === 0 && existingServices.internet.length === 0 && existingServices.cloud.length === 0 && (
                      <p className="text-xs text-semantic-text-disabled italic">
                        No existing services found for this tenant.
                      </p>
                    )}

                    {!existingSectionCollapsed && existingServicesLoaded && (existingServices.ports.length > 0 || existingServices.internet.length > 0) && (
                      <div className="space-y-3">
                        {/* Existing Ports */}
                        {existingServices.ports.map(port => {
                          const parentKey = `port:${port.id}`;
                          const assignedChildren = getExistingParentChildren(parentKey);
                          return (
                            <div
                              key={parentKey}
                              className="border border-blue-200 rounded-lg p-4 bg-blue-50"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <GenericTag label="Port" variant="warning" size="small" />
                                    <GenericTag label="Previous Order" variant="neutral" size="small" />
                                  </div>
                                  <p className="text-sm font-medium text-semantic-text-primary mt-1">
                                    {port.serviceId}
                                  </p>
                                  <p className="text-xs text-semantic-text-secondary">
                                    {port.capacityMbps ? `${port.capacityMbps} Mbps` : ''}
                                    {port.datacenter ? ` | ${port.datacenter.name}` : ''}
                                    {port.isDraft ? ' | Draft' : ''}
                                  </p>
                                </div>
                              </div>

                              {/* Assigned Children */}
                              {assignedChildren.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-blue-200">
                                  <p className="text-xs text-semantic-text-secondary mb-2">
                                    Assigned Services ({assignedChildren.length})
                                  </p>
                                  <div className="space-y-2">
                                    {assignedChildren.map(child => (
                                      <div key={child.id} className="flex items-center justify-between px-3 py-2 bg-white rounded border border-blue-100">
                                        <div className="flex items-center gap-2">
                                          <GenericTag
                                            label={child.product.categoryLabel}
                                            variant={categoryColors[child.product.category] || 'default'}
                                            size="small"
                                          />
                                          <span className="text-sm text-semantic-text-primary">
                                            {child.product.sku || child.product.name}
                                          </span>
                                        </div>
                                        <button
                                          onClick={() => removeChildFromParent(child.id)}
                                          className="text-semantic-error hover:text-red-700 text-xs"
                                          title="Remove assignment"
                                        >
                                          x
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Assign dropdown - IP Blocks are children of Internet, not Port */}
                              {unassignedChildren.filter(c => c.product.category !== 'ip_block').length > 0 && (
                                <div className="mt-3 pt-3 border-t border-dashed border-blue-200">
                                  <GenericDropdown
                                    items={unassignedChildren.filter(c => c.product.category !== 'ip_block').map(child =>
                                      `${child.product.categoryLabel}: ${child.product.sku || child.product.name}${child.destination ? ` -> ${child.destination.name}` : ''}`
                                    )}
                                    defaultLabel="+ Assign a service..."
                                    maxWidthClass="w-full"
                                    dropdownMaxHeightClass="max-h-48"
                                    fixedPosition={true}
                                    onSelect={(selected) => {
                                      const child = unassignedChildren.filter(c => c.product.category !== 'ip_block').find(c =>
                                        `${c.product.categoryLabel}: ${c.product.sku || c.product.name}${c.destination ? ` -> ${c.destination.name}` : ''}` === selected
                                      );
                                      if (child) {
                                        assignChildToExistingParent(child.id, parentKey);
                                      }
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Existing Internet Services */}
                        {existingServices.internet.map(svc => {
                          const parentKey = `internet:${svc.id}`;
                          const assignedChildren = getExistingParentChildren(parentKey);
                          return (
                            <div
                              key={parentKey}
                              className="border border-green-200 rounded-lg p-4 bg-green-50"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <GenericTag label="Internet" variant="success" size="small" />
                                    <GenericTag label="Previous Order" variant="neutral" size="small" />
                                  </div>
                                  <p className="text-sm font-medium text-semantic-text-primary mt-1">
                                    {svc.serviceId}
                                  </p>
                                  <p className="text-xs text-semantic-text-secondary">
                                    {svc.mbps ? `${svc.mbps} Mbps` : ''}
                                    {svc.parentPort ? ` | Port: ${svc.parentPort.serviceId}` : ''}
                                    {svc.parentPort?.datacenter ? ` | DC: ${svc.parentPort.datacenter.name}` : ''}
                                    {svc.isDraft ? ' | Draft' : ''}
                                  </p>
                                </div>
                              </div>

                              {/* Assigned Children */}
                              {assignedChildren.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-green-200">
                                  <p className="text-xs text-semantic-text-secondary mb-2">
                                    Assigned Services ({assignedChildren.length})
                                  </p>
                                  <div className="space-y-2">
                                    {assignedChildren.map(child => (
                                      <div key={child.id} className="flex items-center justify-between px-3 py-2 bg-white rounded border border-green-100">
                                        <div className="flex items-center gap-2">
                                          <GenericTag
                                            label={child.product.categoryLabel}
                                            variant={categoryColors[child.product.category] || 'default'}
                                            size="small"
                                          />
                                          <span className="text-sm text-semantic-text-primary">
                                            {child.product.sku || child.product.name}
                                          </span>
                                        </div>
                                        <button
                                          onClick={() => removeChildFromParent(child.id)}
                                          className="text-semantic-error hover:text-red-700 text-xs"
                                          title="Remove assignment"
                                        >
                                          x
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Assign dropdown - Internet accepts IP Block children */}
                              {unassignedChildren.filter(c => c.product.category === 'ip_block').length > 0 && (
                                <div className="mt-3 pt-3 border-t border-dashed border-green-200">
                                  <GenericDropdown
                                    items={unassignedChildren.filter(c => c.product.category === 'ip_block').map(child =>
                                      `${child.product.categoryLabel}: ${child.product.sku || child.product.name}${child.destination ? ` -> ${child.destination.name}` : ''}`
                                    )}
                                    defaultLabel="+ Assign IP Block..."
                                    maxWidthClass="w-full"
                                    dropdownMaxHeightClass="max-h-48"
                                    fixedPosition={true}
                                    onSelect={(selected) => {
                                      const child = unassignedChildren.filter(c => c.product.category === 'ip_block').find(c =>
                                        `${c.product.categoryLabel}: ${c.product.sku || c.product.name}${c.destination ? ` -> ${c.destination.name}` : ''}` === selected
                                      );
                                      if (child) {
                                        assignChildToExistingParent(child.id, parentKey);
                                      }
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Existing Cloud Services */}
                        {existingServices.cloud.map(svc => {
                          const parentKey = `cloud:${svc.id}`;
                          const assignedChildren = getExistingParentChildren(parentKey);
                          return (
                            <div
                              key={parentKey}
                              className="border border-purple-200 rounded-lg p-4 bg-purple-50"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <GenericTag label="Cloud" variant="neutral" size="small" />
                                    <GenericTag label="Previous Order" variant="neutral" size="small" />
                                  </div>
                                  <p className="text-sm font-medium text-semantic-text-primary mt-1">
                                    {svc.serviceId}
                                  </p>
                                  <p className="text-xs text-semantic-text-secondary">
                                    {svc.mbps ? `${svc.mbps} Mbps` : ''}
                                    {svc.provider ? ` | ${svc.provider}` : ''}
                                    {svc.cloudPlatform ? ` / ${svc.cloudPlatform}` : ''}
                                    {svc.parentPort ? ` | Port: ${svc.parentPort.serviceId}` : ''}
                                    {svc.parentPort?.datacenter ? ` | DC: ${svc.parentPort.datacenter.name}` : ''}
                                    {svc.isDraft ? ' | Draft' : ''}
                                  </p>
                                </div>
                              </div>

                              {/* Assigned Children */}
                              {assignedChildren.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-purple-200">
                                  <p className="text-xs text-semantic-text-secondary mb-2">
                                    Assigned Services ({assignedChildren.length})
                                  </p>
                                  <div className="space-y-2">
                                    {assignedChildren.map(child => (
                                      <div key={child.id} className="flex items-center justify-between px-3 py-2 bg-white rounded border border-purple-100">
                                        <div className="flex items-center gap-2">
                                          <GenericTag
                                            label={child.product.categoryLabel}
                                            variant={categoryColors[child.product.category] || 'default'}
                                            size="small"
                                          />
                                          <span className="text-sm text-semantic-text-primary">
                                            {child.product.sku || child.product.name}
                                          </span>
                                        </div>
                                        <button
                                          onClick={() => removeChildFromParent(child.id)}
                                          className="text-semantic-error hover:text-red-700 text-xs"
                                          title="Remove assignment"
                                        >
                                          x
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Assign dropdown - IP Blocks are children of Internet, not Cloud */}
                              {unassignedChildren.filter(c => c.product.category !== 'ip_block').length > 0 && (
                                <div className="mt-3 pt-3 border-t border-dashed border-purple-200">
                                  <GenericDropdown
                                    items={unassignedChildren.filter(c => c.product.category !== 'ip_block').map(child =>
                                      `${child.product.categoryLabel}: ${child.product.sku || child.product.name}${child.destination ? ` -> ${child.destination.name}` : ''}`
                                    )}
                                    defaultLabel="+ Assign a service..."
                                    maxWidthClass="w-full"
                                    dropdownMaxHeightClass="max-h-48"
                                    fixedPosition={true}
                                    onSelect={(selected) => {
                                      const child = unassignedChildren.filter(c => c.product.category !== 'ip_block').find(c =>
                                        `${c.product.categoryLabel}: ${c.product.sku || c.product.name}${c.destination ? ` -> ${c.destination.name}` : ''}` === selected
                                      );
                                      if (child) {
                                        assignChildToExistingParent(child.id, parentKey);
                                      }
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* FWaaS Section - FWaaS can be parent to Cloud/Internet */}
                  {fwaasLines.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-semantic-border-secondary">
                      <h4 className="text-sm font-medium text-semantic-text-secondary mb-3 uppercase tracking-wide">
                        Firewall as a Service ({fwaasLines.length})
                      </h4>
                      <p className="text-xs text-semantic-text-secondary mb-3">
                        FWaaS acts as intermediate firewall. Assign to a Port, then assign Cloud/Internet services to it.
                      </p>
                      <div className="space-y-3">
                        {fwaasLines.map(fwaas => {
                          const fwaasAssignedChildren = getAssignedChildren(fwaas.id);
                          const fwaasPortAssignment = assignments.find(a => a.childLineIds.includes(fwaas.id));
                          const assignedToPort = fwaasPortAssignment 
                            ? portLines.find(p => p.id === fwaasPortAssignment.parentLineId) 
                            : null;
                          
                          // Get available services for FWaaS (Cloud/Internet only, not yet assigned)
                          const availableForFwaas = unassignedChildren.filter(c => 
                            FWAAS_CHILD_CATEGORIES.includes(c.product.category)
                          );

                          return (
                            <div 
                              key={fwaas.id}
                              className="border border-pink-200 rounded-lg p-4 bg-pink-50"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <GenericTag
                                      label="Firewall"
                                      variant="custom"
                                      size="small"
                                      className="bg-pink-100 text-pink-700 border-pink-300"
                                    />
                                    <span className="text-xs text-semantic-text-secondary">
                                      {fwaas.origin?.name || 'No origin'}
                                    </span>
                                  </div>
                                  <p className="text-sm font-medium text-semantic-text-primary mt-1">
                                    {fwaas.product.sku || fwaas.product.name}
                                  </p>
                                  <p className="text-xs text-semantic-text-secondary">
                                    {fwaas.capacity ? `${fwaas.capacity} Mbps` : ''} 
                                    {fwaas.serviceId ? ` | ${fwaas.serviceId}` : ''}
                                  </p>
                                </div>
                                <span className="text-sm font-medium text-semantic-text-primary">
                                  ${fwaas.subtotal.toLocaleString()}
                                </span>
                              </div>

                              {/* Assign FWaaS to a Port */}
                              <div className="mb-3">
                                <p className="text-xs font-medium text-semantic-text-secondary mb-1">
                                  Connected to Port
                                </p>
                                {assignedToPort ? (
                                  // Read-only display when assigned from Infrastructure section
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-sm text-gray-700">
                                      {assignedToPort.product.sku} ({assignedToPort.serviceId || assignedToPort.id})
                                    </div>
                                    <button
                                      onClick={() => removeChildFromParent(fwaas.id)}
                                      className="px-2 py-2 text-semantic-error hover:text-red-700 text-xs border border-red-200 rounded hover:bg-red-50"
                                      title="Disconnect from Port"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <GenericDropdown
                                    items={portLines.map(p => 
                                      `${p.product.sku || p.product.name} (${p.serviceId || p.id})`
                                    )}
                                    defaultLabel="Select Port..."
                                    maxWidthClass="w-full"
                                    dropdownMaxHeightClass="max-h-36"
                                    fixedPosition={true}
                                    onSelect={(selected) => {
                                      const port = portLines.find(p => 
                                        `${p.product.sku || p.product.name} (${p.serviceId || p.id})` === selected
                                      );
                                      if (port) {
                                        assignChildToParent(fwaas.id, port.id);
                                      }
                                    }}
                                  />
                                )}
                                {assignedToPort && (
                                  <p className="text-xs text-green-600 mt-1">
                                    ✓ Connected to {assignedToPort.product.sku}
                                  </p>
                                )}
                              </div>

                              {/* Children of FWaaS (Cloud/Internet) */}
                              {fwaasAssignedChildren.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-pink-200">
                                  <p className="text-xs text-semantic-text-secondary mb-2">
                                    Services through FWaaS ({fwaasAssignedChildren.length})
                                  </p>
                                  <div className="space-y-2">
                                    {fwaasAssignedChildren.map(child => (
                                      <div 
                                        key={child.id}
                                        className="flex items-center justify-between px-3 py-2 bg-white rounded border border-pink-100"
                                      >
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                          <GenericTag
                                            label={child.product.categoryLabel}
                                            variant={categoryColors[child.product.category] || 'default'}
                                            size="small"
                                          />
                                          <span className="text-xs text-semantic-text-primary truncate">
                                            {child.product.sku || child.product.name}
                                          </span>
                                          {child.destination && (
                                            <span className="text-xs text-semantic-text-secondary">
                                              → {child.destination.name}
                                            </span>
                                          )}
                                        </div>
                                        <button
                                          onClick={() => removeChildFromParent(child.id)}
                                          className="ml-2 text-semantic-error hover:text-red-700 text-xs"
                                          title="Remove from FWaaS"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Add services to FWaaS */}
                              {availableForFwaas.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-dashed border-pink-200">
                                  <GenericDropdown
                                    items={availableForFwaas.map(child => 
                                      `${child.product.categoryLabel}: ${child.product.sku || child.product.name}${child.destination ? ` → ${child.destination.name}` : ''}`
                                    )}
                                    defaultLabel="+ Assign Cloud/Internet service..."
                                    maxWidthClass="w-full"
                                    dropdownMaxHeightClass="max-h-48"
                                    fixedPosition={true}
                                    onSelect={(selected) => {
                                      const child = availableForFwaas.find(c => 
                                        `${c.product.categoryLabel}: ${c.product.sku || c.product.name}${c.destination ? ` → ${c.destination.name}` : ''}` === selected
                                      );
                                      if (child) {
                                        assignChildToParent(child.id, fwaas.id);
                                      }
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Unassigned Children Column */}
                <div>
                  <h4 className="text-sm font-medium text-semantic-text-secondary mb-3 uppercase tracking-wide">
                    Unassigned Services ({unassignedChildren.length})
                  </h4>
                  <div className="space-y-2">
                    {unassignedChildren.length === 0 ? (
                      <p className="text-sm text-semantic-text-disabled italic">
                        {childLines.length === 0 
                          ? 'No child services in this order'
                          : 'All services have been assigned'}
                      </p>
                    ) : (
                      unassignedChildren.map(child => {
                        const isLastMile = child.product.category === 'last_mile';
                        const isCloudConnectivity = child.product.category === 'cloud_connectivity';
                        const cloudConfig = cloudConnectivityConfigs[child.id] || {};
                        
                        return (
                          <div 
                            key={child.id}
                            className="border border-semantic-border-secondary rounded-lg p-3 bg-semantic-bg-secondary"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <GenericTag
                                    label={child.product.categoryLabel}
                                    variant={categoryColors[child.product.category] || 'default'}
                                    size="small"
                                  />
                                  {child.destination && (
                                    <span className="text-xs text-semantic-text-secondary">
                                      → {child.destination.name}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm font-medium text-semantic-text-primary mt-1 truncate">
                                  {child.product.sku || child.product.name}
                                </p>
                                <p className="text-xs text-semantic-text-secondary">
                                  {child.origin?.name || 'No origin'} 
                                  {child.capacity ? ` | ${child.capacity} Mbps` : ''}
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <span className="text-sm font-medium text-semantic-text-primary">
                                  ${child.subtotal.toLocaleString()}
                                </span>
                                {parentLines.length > 0 && (
                                  <GenericDropdown
                                    items={parentLines.map(p => 
                                      `${p.product.categoryLabel}: ${p.origin?.name || 'No location'} (${p.serviceId || p.id})`
                                    )}
                                    defaultLabel="Assign to infrastructure..."
                                    minWidthClass="min-w-[200px]"
                                    maxWidthClass="max-w-[280px]"
                                    dropdownMaxHeightClass="max-h-48"
                                    fixedPosition={true}
                                    onSelect={(selected) => {
                                      const p = parentLines.find(parent => 
                                        `${parent.product.categoryLabel}: ${parent.origin?.name || 'No location'} (${parent.serviceId || parent.id})` === selected
                                      );
                                      if (p) {
                                        assignChildToParent(child.id, p.id);
                                      }
                                    }}
                                  />
                                )}
                              </div>
                            </div>

                            {/* Last Mile - Address Input */}
                            {isLastMile && (
                              <div className="mt-3 pt-3 border-t border-semantic-border-secondary">
                                <label 
                                  htmlFor={`address-${child.id}`}
                                  className="block text-xs font-medium text-semantic-text-secondary mb-1"
                                >
                                  Final Address
                                </label>
                                <input
                                  id={`address-${child.id}`}
                                  type="text"
                                  value={lastMileAddresses[child.id] || ''}
                                  onChange={(e) => handleLastMileAddressChange(child.id, e.target.value)}
                                  placeholder="Enter delivery address..."
                                  className="
                                    w-full px-3 py-2 text-sm
                                    border border-semantic-border-secondary rounded-md
                                    bg-semantic-bg-primary text-semantic-text-primary
                                    placeholder:text-semantic-text-disabled
                                    focus:outline-none focus:ring-2 focus:ring-brand-primary
                                  "
                                />
                              </div>
                            )}

                            {/* Cloud Connectivity - Datacenter, Provider, Cloud */}
                            {isCloudConnectivity && (
                              <div className="mt-3 pt-3 border-t border-semantic-border-secondary space-y-3">
                                {/* Datacenter */}
                                <div>
                                  <p className="text-xs font-medium text-semantic-text-secondary mb-1">
                                    Datacenter
                                  </p>
                                  <GenericDropdown
                                    items={datacenters.map(dc => dc.name)}
                                    defaultLabel={
                                      cloudConfig.datacenter 
                                        ? datacenters.find(dc => dc.datacenterId === cloudConfig.datacenter)?.name || 'Select datacenter...'
                                        : 'Select datacenter...'
                                    }
                                    maxWidthClass="w-full"
                                    dropdownMaxHeightClass="max-h-48"
                                    fixedPosition={true}
                                    onSelect={(selected) => {
                                      const dc = datacenters.find(d => d.name === selected);
                                      if (dc) {
                                        handleCloudConfigChange(child.id, 'datacenter', dc.datacenterId);
                                      }
                                    }}
                                  />
                                </div>
                                {/* Provider */}
                                <div>
                                  <p className="text-xs font-medium text-semantic-text-secondary mb-1">
                                    Provider
                                  </p>
                                  <GenericDropdown
                                    items={[...CLOUD_PROVIDERS]}
                                    defaultLabel={cloudConfig.provider || 'Select provider...'}
                                    maxWidthClass="w-full"
                                    dropdownMaxHeightClass="max-h-48"
                                    fixedPosition={true}
                                    onSelect={(selected) => {
                                      handleCloudConfigChange(child.id, 'provider', selected);
                                    }}
                                  />
                                </div>
                                {/* Cloud Platform */}
                                <div>
                                  <p className="text-xs font-medium text-semantic-text-secondary mb-1">
                                    Cloud Platform
                                  </p>
                                  <GenericDropdown
                                    items={[...CLOUD_PLATFORMS]}
                                    defaultLabel={cloudConfig.cloud || 'Select cloud...'}
                                    maxWidthClass="w-full"
                                    dropdownMaxHeightClass="max-h-48"
                                    fixedPosition={true}
                                    onSelect={(selected) => {
                                      handleCloudConfigChange(child.id, 'cloud', selected);
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* DCI Interconnects Section */}
              {dciLines.length > 0 && (
                <div className="mt-6 pt-4 border-t border-semantic-border-secondary">
                  <h4 className="text-sm font-medium text-semantic-text-secondary mb-3 uppercase tracking-wide">
                    Datacenter Interconnects ({dciLines.length})
                  </h4>
                  <p className="text-xs text-semantic-text-secondary mb-3">
                    DCI connects two ports. Select which ports each DCI should link.
                  </p>
                  <div className="space-y-3">
                    {dciLines.map(dci => {
                      const dciAssignment = dciPortAssignments[dci.id] || { portA: null, portB: null };
                      const isConfigured = dciAssignment.portA !== null && dciAssignment.portB !== null;
                      
                      return (
                        <div 
                          key={dci.id}
                          className="border border-purple-200 rounded-lg p-4 bg-purple-50"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <GenericTag
                                  label="DCI"
                                  variant="custom"
                                  size="small"
                                  className="bg-purple-100 text-purple-700 border-purple-300"
                                />
                                <span className="text-xs text-semantic-text-secondary">
                                  {dci.origin?.name || 'No origin'}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-semantic-text-primary mt-1">
                                {dci.product.sku || dci.product.name}
                              </p>
                              <p className="text-xs text-semantic-text-secondary">
                                {dci.capacity ? `${dci.capacity} Mbps` : ''} 
                                {dci.serviceId ? ` | ${dci.serviceId}` : ''}
                              </p>
                            </div>
                            <span className="text-sm font-medium text-semantic-text-primary">
                              ${dci.subtotal.toLocaleString()}
                            </span>
                          </div>

                          {/* Endpoint Selection (Port, SD-WAN Hub, or Existing Service) */}
                          {(() => {
                            // Build combined endpoint options: current order + existing services
                            // Existing services use negative IDs: -(dbId) to distinguish from Odoo line IDs
                            const existingPorts = existingServices?.ports || [];
                            const endpointOptions: Array<{ id: number; label: string; isExisting: boolean }> = [
                              // Current order endpoints (Port + SD-WAN Hub)
                              ...dciEndpointLines.map(p => ({
                                id: p.id,
                                label: `${p.product.sku || p.product.name} (${p.serviceId || p.id})`,
                                isExisting: false
                              })),
                              // Existing ports from previous orders
                              ...existingPorts
                                .filter(p => !p.isDraft) // Only non-draft existing services
                                .map(p => ({
                                  id: -(p.id), // Negative ID = existing service DB ID
                                  label: `[Previous] ${p.serviceId} - ${p.capacityMbps ? p.capacityMbps + ' Mbps' : ''} ${p.datacenter?.name || 'N/A'}`,
                                  isExisting: true
                                }))
                            ];

                            const resolveLabel = (assignedId: number | null) => {
                              if (assignedId === null) return null;
                              const opt = endpointOptions.find(o => o.id === assignedId);
                              return opt?.label || 'Selected';
                            };

                            return endpointOptions.length >= 2 ? (
                            <div className="grid grid-cols-2 gap-4">
                              {/* Endpoint A */}
                              <div>
                                <p className="text-xs font-medium text-semantic-text-secondary mb-1">Endpoint A</p>
                                <GenericDropdown
                                  items={endpointOptions
                                    .filter(o => o.id !== dciAssignment.portB)
                                    .map(o => o.label)}
                                  defaultLabel={resolveLabel(dciAssignment.portA) || 'Select Endpoint A...'}
                                  maxWidthClass="w-full"
                                  dropdownMaxHeightClass="max-h-48"
                                  fixedPosition={true}
                                  onSelect={(selected) => {
                                    const opt = endpointOptions.find(o => o.label === selected);
                                    if (opt) {
                                      handleDciPortAssignment(dci.id, 'portA', opt.id);
                                    }
                                  }}
                                />
                              </div>
                              {/* Endpoint B */}
                              <div>
                                <p className="text-xs font-medium text-semantic-text-secondary mb-1">Endpoint B</p>
                                <GenericDropdown
                                  items={endpointOptions
                                    .filter(o => o.id !== dciAssignment.portA)
                                    .map(o => o.label)}
                                  defaultLabel={resolveLabel(dciAssignment.portB) || 'Select Endpoint B...'}
                                  maxWidthClass="w-full"
                                  dropdownMaxHeightClass="max-h-48"
                                  fixedPosition={true}
                                  onSelect={(selected) => {
                                    const opt = endpointOptions.find(o => o.label === selected);
                                    if (opt) {
                                      handleDciPortAssignment(dci.id, 'portB', opt.id);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-semantic-text-disabled italic">
                              Need at least 2 ports/hubs to configure DCI
                            </p>
                          );
                          })()}

                          {isConfigured && (
                            <p className="text-xs text-green-600 mt-2">
                              ✓ DCI configured
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Assignment Summary */}
              {assignments.length > 0 && (
                <div className="mt-6 pt-4 border-t border-semantic-border-secondary">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-semantic-text-secondary">
                      {assignments.reduce((sum, a) => sum + a.childLineIds.length, 0)} services assigned to {assignments.length} infrastructure products
                    </span>
                    <span className="text-semantic-text-secondary">
                      {unassignedChildren.length} remaining unassigned
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SD-WAN Branch Hub Assignment */}
          {sdwanBranchLines.length > 0 && sdwanHubLines.length > 0 && (
            <div className="bg-semantic-bg-primary border border-semantic-border-primary rounded-sm p-6">
              <h3 className="text-lg font-semibold text-semantic-text-primary mb-1">
                SD-WAN Branch Hub Assignment
              </h3>
              <p className="text-sm text-semantic-text-secondary mb-4">
                Each branch connects to all hubs. Select the primary hub for each branch (the other becomes secondary).
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-semantic-border-secondary">
                      <th className="text-left py-2 pr-4 text-semantic-text-secondary font-medium">Branch</th>
                      <th className="text-left py-2 pr-4 text-semantic-text-secondary font-medium">Qty</th>
                      {sdwanHubLines.map(hub => (
                        <th key={hub.id} className="text-center py-2 px-3 text-semantic-text-secondary font-medium">
                          <div className="flex flex-col items-center gap-0.5">
                            <span>{hub.product.sku}</span>
                            <span className="text-xs font-normal">{hub.origin?.name || 'N/A'}</span>
                            {hub.serviceId && (
                              <span className="text-xs font-mono text-semantic-success">{hub.serviceId}</span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sdwanBranchLines.map(branch => {
                      const primaryHubId = sdwanBranchPrimaryHub[branch.id];
                      return (
                        <tr key={branch.id} className="border-b border-semantic-border-secondary last:border-b-0">
                          <td className="py-3 pr-4">
                            <p className="font-medium text-semantic-text-primary">{branch.product.sku}</p>
                            <p className="text-xs text-semantic-text-secondary truncate max-w-xs">{branch.product.name}</p>
                          </td>
                          <td className="py-3 pr-4 text-semantic-text-primary">
                            x{branch.quantity}
                          </td>
                          {sdwanHubLines.map(hub => {
                            const isPrimary = primaryHubId === hub.id;
                            const isSecondary = primaryHubId && primaryHubId !== hub.id;
                            return (
                              <td key={hub.id} className="py-3 px-3 text-center">
                                <button
                                  onClick={() => setSdwanBranchPrimaryHub(prev => ({ ...prev, [branch.id]: hub.id }))}
                                  className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                    isPrimary
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : isSecondary
                                        ? 'bg-amber-100 text-amber-700 border-amber-300'
                                        : 'bg-semantic-bg-primary text-semantic-text-secondary border-semantic-border-secondary hover:border-blue-400'
                                  }`}
                                >
                                  {isPrimary ? 'Primary' : isSecondary ? 'Secondary' : 'Set Primary'}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Service Architecture Graph */}
          {(parentLines.length > 0 || childLines.length > 0 || dciLines.length > 0) && (
            <div className="bg-semantic-bg-primary border border-semantic-border-primary rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-semantic-text-primary">
                    Service Architecture
                  </h3>
                  <p className="text-sm text-semantic-text-secondary mt-1">
                    Visual representation of service distribution by location
                  </p>
                </div>
              </div>
              <div className="h-[400px] border border-semantic-border-secondary rounded-lg overflow-hidden">
                <ServiceArchitectureGraph
                  ref={graphRef}
                  orderLines={order.orderLines}
                  assignments={assignments}
                  parentDatacenterMap={parentDatacenterMap}
                  datacenters={datacenters}
                  dciPortAssignments={dciPortAssignments}
                  sdwanHubRoles={sdwanHubRoles}
                  sdwanBranchPrimaryHub={sdwanBranchPrimaryHub}
                />
              </div>
            </div>
          )}

          {/* Submit Design Section */}
          {(parentLines.length > 0 || childLines.length > 0 || dciLines.length > 0) && (
            <div className="bg-semantic-bg-primary border border-semantic-border-primary rounded-sm p-6">
              {/* Already Submitted Banner */}
              {isDesignSubmitted && designStatus && (
                <div className={`mb-4 p-4 border rounded-md ${designStatus.isLegacy ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex items-start gap-3">
                    <svg className={`w-5 h-5 mt-0.5 ${designStatus.isLegacy ? 'text-amber-600' : 'text-green-600'}`} fill="currentColor" viewBox="0 0 20 20">
                      {designStatus.isLegacy ? (
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      ) : (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      )}
                    </svg>
                    <div>
                      <p className={`text-sm font-medium ${designStatus.isLegacy ? 'text-amber-800' : 'text-green-800'}`}>
                        {designStatus.isLegacy ? 'Legacy Order (Already Processed)' : 'Design Already Submitted'}
                      </p>
                      <p className={`text-sm mt-1 ${designStatus.isLegacy ? 'text-amber-700' : 'text-green-700'}`}>
                        {designStatus.isLegacy 
                          ? 'This order was processed before the Presales Designer feature. Configuration data is not available.'
                          : `This design was submitted on ${designStatus.designSubmittedAt ? new Date(designStatus.designSubmittedAt).toLocaleString() : 'unknown date'}. The configuration shown is read-only.`
                        }
                      </p>
                      {!designStatus.isLegacy && designStatus.savedDesign?.jiraTickets && designStatus.savedDesign.jiraTickets.length > 0 && (
                        <p className="text-xs text-green-600 mt-1">
                          {designStatus.savedDesign.jiraTickets.length} Jira ticket{designStatus.savedDesign.jiraTickets.length !== 1 ? 's' : ''} created
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Presales Comment */}
              {!isDesignSubmitted && !submitSuccess && (
                <div className="mb-4">
                  <label
                    htmlFor="presales-comment"
                    className="block text-sm font-medium text-semantic-text-primary mb-1"
                  >
                    Additional Comments for Jira Tickets
                  </label>
                  <p className="text-xs text-semantic-text-secondary mb-2">
                    This note will be added as an internal comment on every Jira ticket created for this order.
                  </p>
                  <textarea
                    id="presales-comment"
                    value={presalesComment}
                    onChange={(e) => setPresalesComment(e.target.value)}
                    placeholder="Add any additional context, instructions, or observations for the provisioning team..."
                    rows={3}
                    maxLength={2000}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-semantic-border-secondary rounded-lg text-sm text-semantic-text-primary bg-semantic-bg-primary placeholder-semantic-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <p className="text-xs text-semantic-text-tertiary mt-1 text-right">
                    {presalesComment.length} / 2000
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-semantic-text-primary">
                    {isDesignSubmitted ? 'Design Submitted' : 'Submit Design'}
                  </h3>
                  <p className="text-sm text-semantic-text-secondary mt-1">
                    {isDesignSubmitted 
                      ? 'Service configuration has been finalized' 
                      : 'Finalize service configuration and create Jira tickets'}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  {/* Validation Summary */}
                  {(() => {
                    const validation = validateAssignments();
                    return (
                      <div className="flex items-center gap-2 text-sm">
                        {validation.isValid ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Ready to submit
                          </span>
                        ) : (
                          <span className="text-red-600 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            {validation.errors.length} issue{validation.errors.length !== 1 ? 's' : ''} found
                          </span>
                        )}
                        {validation.warnings.length > 0 && (
                          <span className="text-yellow-600 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {validation.warnings.length} warning{validation.warnings.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Export Buttons */}
                  <div className="flex items-center gap-2 border-r border-semantic-border-secondary pr-4 mr-2">
                    <button
                      onClick={() => graphRef.current?.exportDiagram('svg')}
                      className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg shadow-sm text-xs font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-400 transition-colors flex items-center gap-1.5"
                      title="Export as SVG"
                    >
                      <span>📄</span>
                      SVG
                    </button>
                    <button
                      onClick={() => graphRef.current?.exportDiagram('png')}
                      className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg shadow-sm text-xs font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-400 transition-colors flex items-center gap-1.5"
                      title="Export as PNG"
                    >
                      <span>🖼️</span>
                      PNG
                    </button>
                  </div>
                  
                  {/* Dry Run Toggle */}
                  <label className="flex items-center gap-2 cursor-pointer" title="Preview tickets without creating them">
                    <input
                      type="checkbox"
                      checked={isDryRunMode}
                      onChange={(e) => {
                        setIsDryRunMode(e.target.checked);
                        setDryRunPreview(null);
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-xs font-medium text-amber-600">Dry Run</span>
                  </label>
                  
                  {/* Submit Button */}
                  <GenericButton
                    label={
                      isDesignSubmitted || submitSuccess 
                        ? 'Submitted' 
                        : isSubmitting 
                          ? isDryRunMode ? 'Previewing...' : 'Creating Tickets...'
                          : isDryRunMode ? 'Preview Tickets' : 'Submit Design'
                    }
                    onClick={handleSubmitAssignments}
                    variant={isDryRunMode ? 'outlined' : 'brand'}
                    size="md"
                    disabled={isSubmitting || !validateAssignments().isValid || isDesignSubmitted || !!submitSuccess}
                  />
                </div>
              </div>
              
              {/* Error Message */}
              {submitError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-red-800 mb-1">Unable to Submit Design</p>
                      <p className="text-sm text-red-700 whitespace-pre-line">{submitError}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Dry Run Preview */}
              {dryRunPreview && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-amber-800">
                        DRY RUN - Preview Only (No tickets created)
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        {dryRunPreview.servicesToTicket} ticket{dryRunPreview.servicesToTicket !== 1 ? 's' : ''} would be processed
                        {dryRunPreview.ticketPreviews.some(t => t.action === 'would_update') && (
                          <span className="ml-1">
                            ({dryRunPreview.ticketPreviews.filter(t => t.action === 'would_update').length} update{dryRunPreview.ticketPreviews.filter(t => t.action === 'would_update').length !== 1 ? 's' : ''}, {dryRunPreview.ticketPreviews.filter(t => t.action !== 'would_update').length} new)
                          </span>
                        )}
                      </p>
                      
                      {/* Warnings - Show why no tickets were created */}
                      {dryRunPreview.warnings && dryRunPreview.warnings.length > 0 && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-xs font-semibold text-red-800 mb-2">
                            Issues detected ({dryRunPreview.warnings.length}):
                          </p>
                          <ul className="space-y-1.5">
                            {dryRunPreview.warnings.map((warning, idx) => (
                              <li key={idx} className="text-xs text-red-700 flex items-start gap-2">
                                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span>{warning.message}</span>
                              </li>
                            ))}
                          </ul>
                          <p className="text-xs text-red-600 mt-3 pt-2 border-t border-red-200">
                            <strong>Solution:</strong> The order must be processed by the Odoo webhook first. 
                            This creates the service records in the database that are needed to generate Jira tickets.
                          </p>
                        </div>
                      )}
                      
                      {dryRunPreview.ticketPreviews.length > 0 && (
                        <div className="mt-3 space-y-3 max-h-[500px] overflow-y-auto">
                          <p className="text-xs font-medium text-amber-700">Ticket Previews:</p>
                          {dryRunPreview.ticketPreviews.map((ticket) => {
                            const isUpdate = ticket.action === 'would_update';
                            return (
                            <div 
                              key={ticket.serviceId} 
                              className={`p-3 bg-white border rounded-md text-xs ${isUpdate ? 'border-blue-200' : 'border-amber-200'}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className={`font-semibold ${isUpdate ? 'text-blue-800' : 'text-amber-800'}`}>
                                  {ticket.serviceType} - {ticket.serviceId}
                                </span>
                                <span className={`px-2 py-0.5 rounded text-xs ${isUpdate ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {isUpdate ? `WOULD UPDATE ${ticket.ticketKey}` : 'WOULD CREATE'}
                                </span>
                              </div>
                              
                              {/* Summary (Title) */}
                              <div className="mb-2">
                                <span className="text-gray-500 font-medium">Title:</span>
                                <p className="text-gray-800 font-medium mt-0.5 break-all">
                                  {ticket.jiraBody?.requestFieldValues?.summary || 'N/A'}
                                </p>
                              </div>
                              
                              {/* Description (Body) - show updated description for updates */}
                              <div>
                                <span className="text-gray-500 font-medium">
                                  {isUpdate ? 'Updated Description:' : 'Description:'}
                                </span>
                                <pre className="mt-1 p-2 bg-gray-50 rounded text-gray-700 overflow-x-auto whitespace-pre-wrap text-[11px] leading-relaxed">
                                  {isUpdate && ticket.previewDescription
                                    ? ticket.previewDescription
                                    : ticket.jiraBody?.requestFieldValues?.description || 'N/A'}
                                </pre>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="mt-4 flex gap-2">
                        <button
                          onClick={() => {
                            setDryRunPreview(null);
                            setIsDryRunMode(false);
                          }}
                          className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded hover:bg-amber-700 transition-colors"
                        >
                          Close & Submit for Real
                        </button>
                        <button
                          onClick={() => setDryRunPreview(null)}
                          className="px-3 py-1.5 bg-white border border-amber-300 text-amber-700 text-xs font-medium rounded hover:bg-amber-50 transition-colors"
                        >
                          Close Preview
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Success Message */}
              {submitSuccess && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-medium text-green-800">
                        Design submitted - {submitSuccess.ticketsCreated} Jira ticket{submitSuccess.ticketsCreated !== 1 ? 's' : ''} processed
                      </span>
                    </div>
                    <GenericButton
                      label="Back to Order List"
                      onClick={handleBackToList}
                      variant="outlined"
                      size="sm"
                    />
                  </div>
                  {submitSuccess.tickets.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {submitSuccess.tickets.map((t, idx) => (
                        <p key={idx} className="text-xs text-green-700 ml-7">
                          {t.action === 'updated' ? 'Updated' : 'Created'}: {t.ticketKey} ({t.serviceType})
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Validation Details (collapsible) */}
              {(() => {
                const validation = validateAssignments();
                if (validation.errors.length === 0 && validation.warnings.length === 0) return null;
                
                return (
                  <div className="mt-4 space-y-2">
                    {validation.errors.length > 0 && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm font-medium text-red-700 mb-2">Errors (must fix):</p>
                        <ul className="text-sm text-red-600 space-y-1 list-disc list-inside">
                          {validation.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {validation.warnings.length > 0 && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-sm font-medium text-yellow-700 mb-2">Warnings (optional):</p>
                        <ul className="text-sm text-yellow-600 space-y-1 list-disc list-inside">
                          {validation.warnings.map((warn, i) => (
                            <li key={i}>{warn}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Order Lines by Category (excluding NRC products) */}
          <div className="bg-semantic-bg-primary border border-semantic-border-primary rounded-sm p-6">
            <h3 className="text-lg font-semibold text-semantic-text-primary mb-4">
              Order Lines by Category
            </h3>
            <div className="space-y-6">
              {Object.entries(order.linesByCategory)
                .map(([category, group]) => {
                  // Filter out NRC products from display
                  const filteredLines = group.lines.filter(
                    (line: OdooOrderLine) => !line.product.sku?.endsWith('-NRC')
                  );
                  // Calculate subtotal without NRCs
                  const filteredSubtotal = filteredLines.reduce(
                    (sum: number, line: OdooOrderLine) => sum + line.subtotal, 0
                  );
                  return { category, group, filteredLines, filteredSubtotal };
                })
                .filter(({ filteredLines }) => filteredLines.length > 0) // Hide empty categories
                .map(({ category, group, filteredLines, filteredSubtotal }) => (
                <div key={category} className="border-b border-semantic-border-secondary pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <GenericTag
                        label={group.label}
                        variant={categoryColors[category as ProductCategory] || 'default'}
                        size="small"
                      />
                      <span className="text-sm text-semantic-text-secondary">
                        ({filteredLines.length} items)
                      </span>
                    </div>
                    <span className="text-sm font-medium text-semantic-text-primary">
                      ${filteredSubtotal.toLocaleString()}
                    </span>
                  </div>
                  
                  {/* Lines Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-semantic-text-secondary border-b border-semantic-border-secondary">
                          <th className="pb-2 pr-4">Product</th>
                          <th className="pb-2 pr-4 text-right">Capacity</th>
                          <th className="pb-2 pr-4 text-right">Term</th>
                          <th className="pb-2 pr-4">Service ID</th>
                          <th className="pb-2 text-right">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLines.map((line: OdooOrderLine) => (
                          <tr key={line.id} className="border-b border-semantic-border-secondary last:border-0">
                            <td className="py-2 pr-4">
                              <div>
                                <p className="font-medium text-semantic-text-primary">
                                  {line.product.sku || '-'}
                                </p>
                                <p className="text-xs text-semantic-text-secondary truncate max-w-xs">
                                  {line.product.name}
                                </p>
                              </div>
                            </td>
                            <td className="py-2 pr-4 text-right text-semantic-text-primary">
                              {line.capacity ? `${line.capacity} Mbps` : '-'}
                            </td>
                            <td className="py-2 pr-4 text-right text-semantic-text-primary">
                              {line.termMonths ? `${line.termMonths}m` : '-'}
                            </td>
                            <td className="py-2 pr-4">
                              {(() => {
                                // Services that don't get a service ID (dependencies, not standalone services)
                                const noServiceIdCategories = ['last_mile', 'cpe', 'sdwan_branch'];
                                const categoryHasNoServiceId = noServiceIdCategories.includes(line.product.category);
                                
                                if (line.isProvisioned && line.serviceId) {
                                  return (
                                    <span className="text-semantic-success font-mono text-xs">
                                      {line.serviceId}
                                    </span>
                                  );
                                }
                                if (categoryHasNoServiceId) {
                                  return <span className="text-semantic-text-disabled text-xs">N/A</span>;
                                }
                                return <span className="text-semantic-warning text-xs">Pending</span>;
                              })()}
                            </td>
                            <td className="py-2 text-right font-medium text-semantic-text-primary">
                              ${line.subtotal.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}

    </div>
  );
}
